import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
    Modal,
    Form,
    Input,
    Button,
    Select,
    Flex,
    Typography,
    message,
    Radio,
    Space,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
const { Title } = Typography;

const AddUserModal = forwardRef(({ onSuccess }, ref) => {
    const [form] = Form.useForm();
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tenantList, setTenantList] = useState([]);
    const [vendorList, setVendorList] = useState([]);
    const [vendorSelectValue, setVendorSelectValue] = useState(null);
    const [vendorValue, setVendorValue] = useState(null);
    const [vendorId, setVendorId] = useState(null);
    const [error, setError] = useState(null);

    const passwordRules = [
        { required: true, message: "Please enter the password." },
        { min: 6, message: "The password must be at least 6 characters." },
        { max: 20, message: "The password has a maximum of 20 characters." },
        {
            validator: (_, value) =>
                !value || !value.includes(",")
                    ? Promise.resolve()
                    : Promise.reject(new Error("Password cannot contain commas.")),
        },
    ];

    const layout = {
        labelCol: {
            style: {
                width: "150px",
            },
        },
        wrapperCol: {
            style: {
                flex: 1,
            },
        },
    };

    // 获取tenant列表
    const TenantList = async (searchValue) => {
        try {
            const params = {
                content: searchValue,
            };
            const response = await http.get(api.getTenantList, { params });
            if (response.code === "1") {
                const list = (response.data || []).map((item) => ({
                    ...item,
                    value: item.tenantId,
                    label: item.propUnitTntStatus,
                }));
                setTenantList(list);
            }
        } catch (error) {
            console.error("msg:", error);
        }
    };

    // 获取vendor列表
    const vendorFun = async (content, vendorTypeParam) => {
        try {
            let url = `${api.vendorType}?content=${encodeURIComponent(content || "")}`;

            if (vendorTypeParam === 1) {
                url += "&vendorType=Maintenance";
            } else if (vendorTypeParam === 2) {
                url += "&vendorType=Contractor";
            }
            const response = await http.get(url);
            if (response && response.data) {
                const formattedData = response.data.map((item) => ({
                    value: Number(item.vendorId),
                    label: item.vendorName,
                    id: item.vendid,
                    vendorName: item.vendorName,
                }));
                setVendorList(formattedData);
            }
        } catch (err) {
            setError(err);
        }
    };

    const payChange = (e) => {
        const selectedValue = e.target.value;
        setVendorSelectValue(selectedValue);
        setVendorList([]);
        setVendorValue(null);
        vendorFun("", selectedValue);
    };

    const stepSearchChange = (value) => {
        vendorFun(value, vendorSelectValue);
    };

    const stepSearchChangeTenant = (value) => {
        TenantList(value);
    };

    const stepChange = (value, option) => {
        setVendorValue(option.label);
        setVendorId(option.value);
        form.setFieldsValue({
            VendorValue: option.label,
            vendorId: option.value,
        });
    };

    const onTypeChange = (value, option) => {
        // 如果需要处理propId等逻辑，可以在这里添加
    };

    const onFinishAdd = async (values) => {
        try {
            setLoading(true);
            const submitData = {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                password: values.password,
                confirmPassword: values.confirmPassword,
                userType: values.userType,
                phone: values.phone,
            };

            if (values.userType === 2) {
                submitData.vendorId = values.vendorId;
            } else if (values.userType === 1) {
                submitData.tenantId = values.tenantId;
                submitData.propId = values.propId || "";
                submitData.unitId = values.unitId || "";
                submitData.propUnit = values.propUnit || "";
                submitData.address = values.address || "";
                submitData.city = values.city || "";
            }
            const response = await http.post(`${api.getAppUserAdd}`, submitData);
            if (response.code === "1") {
                message.success("Add app user successfully");
                setVisible(false);
                form.resetFields();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                message.error(response.msg || "Update failed");
            }
        } catch (error) {
            message.error("Submit an error and try again later.");
        } finally {
            setLoading(false);
        }
    };

    const showModal = () => {
        form.resetFields();
        setVendorSelectValue(null);
        setVendorList([]);
        setVendorValue(null);
        setTenantList([]);
        setVisible(true);
        TenantList();
    };

    const handleCancel = () => {
        setVisible(false);
        form.resetFields();
        setVendorSelectValue(null);
        setVendorList([]);
        setVendorValue(null);
        setTenantList([]);
    };

    useImperativeHandle(ref, () => ({
        showModal,
    }));

    return (
        <Modal
            title={
                <Flex justify="flex-start" align="flex-start" className="model_title">
                    <EditOutlined className="model_title_mr10" />
                    <Title level={4} className="mg">
                        ADD APP USER
                    </Title>
                </Flex>
            }
            width="50%"
            open={visible}
            onCancel={handleCancel}
            footer={
                <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
                    <Button key="back" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={() => form.submit()}
                    >
                        Submit
                    </Button>
                </div>
            }
        >
            <Flex justify="space-between" align="flex-start">
                <div className="model_top" style={{ width: "100%" }}>
                    <div className="model_content">
                        <Form
                            {...layout}
                            name="AddForm"
                            onFinish={onFinishAdd}
                            form={form}
                            labelCol={{
                                style: {
                                    width: "150px",
                                },
                            }}
                            wrapperCol={{
                                style: {
                                    flex: 1,
                                },
                            }}
                        >
                            <Form.Item
                                name="userType"
                                label="User Type"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select User Type.",
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Please select User Type"
                                    onChange={() => {
                                        form.setFieldsValue({
                                            vendorId: undefined,
                                            tenantId: undefined,
                                        });
                                    }}
                                >
                                    <Select.Option value={1}>Tenant</Select.Option>
                                    <Select.Option value={2}>Vendor</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter email.",
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter email." allowClear />
                            </Form.Item>
                            <Form.Item
                                name="firstName"
                                label="First Name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter firstName.",
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter firstName." allowClear />
                            </Form.Item>
                            <Form.Item
                                name="lastName"
                                label="Last Name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter lastName.",
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter lastName." allowClear />
                            </Form.Item>

                            <Form.Item
                                name="phone"
                                label="Phone"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter phone.",
                                    },
                                    {
                                        validator: commonFn.usPhoneValidator,
                                    },
                                ]}
                            >
                                <Input placeholder="Please enter phone." allowClear />
                            </Form.Item>
                            <Form.Item name="password" label="password" rules={passwordRules}>
                                <Input.Password
                                    placeholder="Please enter password."
                                    allowClear
                                    visibilityToggle
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label="ConfirmPassword"
                                dependencies={["password"]}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter confirmPassword.",
                                    },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue("password") === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(
                                                new Error(
                                                    "The passwords entered twice are inconsistent."
                                                )
                                            );
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    placeholder="Please enter confirmPassword."
                                    allowClear
                                    visibilityToggle
                                />
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.userType !== currentValues.userType
                                }
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue("userType") === 2 && (
                                        <>
                                            <Form.Item name="vendorType" label="Type">
                                                <Radio.Group
                                                    onChange={payChange}
                                                    value={vendorSelectValue}
                                                >
                                                    <Radio value={1}>Monthly Maintenance</Radio>
                                                    <Radio value={2}>Contractor(PO)</Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        </>
                                    )
                                }
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.userType !== currentValues.userType
                                }
                            >
                                {({ getFieldValue }) => {
                                    const userType = getFieldValue("userType");
                                    return userType === 2 ? (
                                        <Form.Item
                                            name="vendorId"
                                            label="Vendor"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please enter Vendor.",
                                                },
                                            ]}
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                onChange={stepChange}
                                                onSearch={stepSearchChange}
                                                placeholder="Select Vendor"
                                                optionFilterProp="label"
                                                value={vendorValue}
                                                filterOption={false}
                                                options={vendorList}
                                                optionRender={(option) => (
                                                    <Space>
                                                        <span role="img" aria-label={option.data.vendorName}>
                                                            {option.data.vendorName}
                                                        </span>
                                                    </Space>
                                                )}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item
                                            name="tenantId"
                                            label="Tenant"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please enter Tenant.",
                                                },
                                            ]}
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Select a tenant"
                                                onChange={onTypeChange}
                                                onSearch={stepSearchChangeTenant}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "")
                                                        .toLowerCase()
                                                        .includes(input.toLowerCase())
                                                }
                                                options={tenantList}
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </Flex>
        </Modal>
    );
});

export default AddUserModal;